import React from 'react';
import { motion } from 'framer-motion';
import { ArtistPersonality, ArtistMood, ArtistAction, NeedsState } from '@/types/career-sim';
import { characterAnimations } from '@/utils/animationVariants';

interface EnlargedCharacterProps {
  personality: ArtistPersonality;
  mood: ArtistMood;
  action: ArtistAction | null;
  needs: NeedsState;
}

const EnlargedCharacter: React.FC<EnlargedCharacterProps> = ({
  personality,
  mood,
  action,
  needs
}) => {
  // Get character emoji based on personality and mood
  const getCharacterEmoji = () => {
    if (needs.energy < 20) return '😴';
    if (needs.mentalHealth < 25) return '😰';
    if (needs.creativity > 80) return '🎨';
    
    if (action) {
      if (action.id.includes('record') || action.id.includes('create')) return '🎵';
      if (action.id.includes('perform')) return '🎤';
      if (action.id.includes('network')) return '🤝';
      if (action.id.includes('rest')) return '😴';
    }

    switch (mood) {
      case 'energetic': return '⚡';
      case 'creative': return '🎨';
      case 'happy': return '😊';
      case 'stressed': return '😰';
      case 'frustrated': return '😤';
      case 'exhausted': return '😴';
      case 'burnt-out': return '🔥';
      default: return '🎵';
    }
  };

  // Get speech bubble based on current state
  const getSpeechBubble = () => {
    if (needs.energy < 15) return "I need rest...";
    if (needs.mentalHealth < 20) return "This is too much...";
    if (needs.creativity > 85) return "I'm feeling inspired!";
    
    if (action) {
      if (action.id.includes('record')) return "Let's create magic!";
      if (action.id.includes('perform')) return "Time to shine!";
      if (action.id.includes('network')) return "Making connections!";
      if (action.id.includes('rest')) return "Taking a break...";
      return "Working hard!";
    }

    if (mood === 'energetic') return "Let's do this!";
    if (mood === 'creative') return "I have so many ideas!";
    if (mood === 'happy') return "Feeling great!";
    if (mood === 'frustrated') return "Ugh, this is tough...";
    
    return "What's next?";
  };

  // Particle effects based on state
  const getParticles = () => {
    const particles = [];
    
    // Tired particles
    if (needs.energy < 30) {
      for (let i = 0; i < 3; i++) {
        particles.push(
          <motion.div
            key={`tired-${i}`}
            className="absolute text-2xl"
            initial={{ opacity: 0, y: 0 }}
            animate={{ 
              opacity: [0, 0.6, 0],
              y: [-20, -40],
              x: [0, (i - 1) * 10]
            }}
            transition={{ 
              repeat: Infinity,
              duration: 2,
              delay: i * 0.5
            }}
            style={{ top: '20%', left: '50%' }}
          >
            💤
          </motion.div>
        );
      }
    }

    // Creative sparkles
    if (needs.creativity > 80 && !action) {
      for (let i = 0; i < 5; i++) {
        particles.push(
          <motion.div
            key={`sparkle-${i}`}
            className="absolute text-xl"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
              rotate: [0, 360]
            }}
            transition={{ 
              repeat: Infinity,
              duration: 2,
              delay: i * 0.3
            }}
            style={{ 
              top: `${20 + i * 15}%`, 
              left: `${30 + (i % 2) * 40}%` 
            }}
          >
            ✨
          </motion.div>
        );
      }
    }

    // Money particles when performing
    if (action?.outcomes.funds && action.outcomes.funds > 0) {
      for (let i = 0; i < 4; i++) {
        particles.push(
          <motion.div
            key={`money-${i}`}
            className="absolute text-xl"
            animate={{ 
              opacity: [1, 0],
              y: [0, -50],
              x: [(i - 2) * 15, (i - 2) * 20]
            }}
            transition={{ 
              repeat: Infinity,
              duration: 1.5,
              delay: i * 0.2
            }}
            style={{ top: '50%', left: '50%' }}
          >
            💰
          </motion.div>
        );
      }
    }

    return particles;
  };

  return (
    <div className="relative">
      {/* Speech bubble */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute -top-16 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-md text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap border border-neon-purple/30 z-20"
      >
        {getSpeechBubble()}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-black/80 rotate-45 border-r border-b border-neon-purple/30" />
      </motion.div>

      {/* Main character container */}
      <motion.div
        className="relative"
        variants={characterAnimations}
        initial="idle"
        animate={action ? "active" : needs.energy < 20 ? "exhausted" : "idle"}
      >
        {/* Character backdrop/stage */}
        <div className="relative w-[320px] h-[320px] rounded-full bg-gradient-to-b from-neon-purple/20 to-neon-blue/20 border-2 border-neon-purple/40 flex items-center justify-center backdrop-blur-sm overflow-hidden">
          {/* Animated gradient background */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-neon-purple/30 via-neon-blue/20 to-neon-pink/30"
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />

          {/* Giant character emoji */}
          <motion.div
            className="relative z-10 text-[180px] select-none"
            animate={
              action 
                ? { rotate: [-5, 5, -5], y: [-10, 0, -10] }
                : needs.energy < 20
                ? { y: [0, 5, 0] }
                : { y: [-3, 3, -3] }
            }
            transition={{ 
              duration: action ? 1 : 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {getCharacterEmoji()}
          </motion.div>

          {/* Particles */}
          {getParticles()}
        </div>

        {/* Name and personality badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-neon-purple to-neon-blue px-6 py-2 rounded-full text-white font-bold text-lg shadow-neon z-20 whitespace-nowrap"
        >
          {personality.name}
        </motion.div>
      </motion.div>

      {/* Action indicator */}
      {action && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          className="absolute -bottom-12 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md px-4 py-2 rounded-full text-sm text-white/90 border border-neon-blue/30"
        >
          <motion.span
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {action.name}...
          </motion.span>
        </motion.div>
      )}
    </div>
  );
};

export default EnlargedCharacter;
